<template>
  <section id="step4" class="p-content-plan-manual-step">
    <div class="l-container">
      <h2 class="c-title--x-large">
        STEP4 記事のタイトル・ディスクプリプションを作成する
      </h2>
      <section id="step4-1" class="p-content-plan-manual-step--sub">
        <h3 class="c-title--large">
          STEP4-1 上位サイトを参考にタイトルを設定する
        </h3>
        <div class="l-rows">
          <p>
            タイトルは、多くの情報が並んでいる検索結果画面においてユーザーから選んでもらうために非常に重要です。
          </p>
          <div class="c-panel">
            <p class="c-panel__title">ポイント</p>
            <ol class="c-list--decimal c-list-marker--bold l-rows--small">
              <li>
                <p class="c-text--bold">
                  対策キーワードを単語レベルに分解してタイトルに入れる
                </p>
                <p class="c-text--small c-text--note">
                  例：対策キーワードが「SEO
                  ツール」の場合、「SEO」と「ツール」をそれぞれ必ずタイトルに含める
                </p>
              </li>
              <li>
                <p class="c-text--bold">
                  全角32文字に収める（末尾のサイト名など、サイト内で共通する部分を除く）
                </p>
                <p class="c-text--small c-text--note">
                  全角32文字を超えてしまうと、検索結果一覧でタイトルが一部見えなくなってしまう可能性があります。
                </p>
              </li>
              <li>
                <p class="c-text--bold">
                  クリックしたくなるようなキャッチワードを入れる
                </p>
                <p class="c-text--small c-text--note">
                  ユーザーが読みたいと思うような表現を、ユーザー視点で検討し使用しましょう。現在上位表示されている競合サイトのタイトルを参考にして、ユーザーが何を魅力的に感じているかを分析することもおすすめです。<br />
                  例：【2022年版】無料&amp;有料おすすめSEOツール22選を目的別にご紹介！
                </p>
              </li>
              <li>
                <p class="c-text--bold">
                  はじめは仮で作成しておき、記事本文と行き来しながら最終的に完成させる
                </p>
              </li>
            </ol>
          </div>
          <p>
            上位サイトの情報を参考に共通するもの＝ユーザーにとって魅力的な要素を分析し、タイトルを検討しましょう。
          </p>
        </div>
        <div class="l-section">
          <h4 class="c-title--medium">
            「{{ keyword }}」で1位～10位サイトのタイトル一覧
          </h4>
          <div class="p-content-plan-manual-tdh-words">
            <ListContentManualText
              :tdhWordsItems="tdhWordsItems"
              scope="title"
            />
            <section>
              <h5 class="c-title--small">使用頻度の高いキーワード</h5>
              <ListContentManualKeyword
                :tdhWordsItems="tdhWordsItems"
                scope="title"
              />
            </section>
          </div>
        </div>
      </section>
      <section id="step4-2" class="p-content-plan-manual-step--sub">
        <h3 class="c-title--large">
          STEP4-2 上位サイトを参考にディスクプリプションを設定する
        </h3>
        <div class="l-rows">
          <p>
            ディスクリプションは、タイトルと同様に検索結果画面に表示されるため、クリック率に大きく影響します。<br />
            直接的にランキングシグナルに影響を与えるタグではありませんが、ユーザーのクリック率を上げるためにしっかりと検討し設定しましょう。
          </p>
          <div class="c-panel">
            <p class="c-panel__title">ポイント</p>
            <ol class="c-list--decimal c-list-marker--bold l-rows--small">
              <li>
                <p class="c-text--bold">
                  全角120文字に収める
                </p>
                <p class="c-text--small c-text--note">
                  PC版の検索結果であれば全角120文字程度が表示されます。ただし、サイトの主な流入元デバイスがスマートフォンの場合は、70文字以内に収めていただくことを推奨いたします。
                </p>
              </li>
              <li>
                <p class="c-text--bold">
                  検索キーワードをディスクリプションの文章内に入れるようにする
                </p>
                <p class="c-text--small c-text--note">
                  ディスクリプションに含まれている検索キーワードは太字で表示されるため、検索キーワードをディスクリプションに含むことで、ユーザーの興味を引きクリックを促すことができます。
                </p>
              </li>
              <li>
                <p class="c-text--bold">
                  ページの内容を分かりやすく表す
                </p>
                <p class="c-text--small c-text--note">
                  どのような内容であるかを簡潔に・分かりやすく記載することで、クリック率が向上しやすくなります。
                </p>
              </li>
              <li>
                <p class="c-text--bold">
                  はじめは仮で作成しておき、記事本文と行き来しながら最終的に完成させる
                </p>
              </li>
            </ol>
          </div>
          <p>
            上位サイトの情報を参考に共通するもの＝ユーザーにとって魅力的な要素を分析し、ディスクリプションを検討しましょう。
          </p>
        </div>
        <div class="l-section">
          <h4 class="c-title--medium">
            「{{ keyword }}」で1位～10位サイトのディスクリプション一覧
          </h4>
          <div class="p-content-plan-manual-tdh-words">
            <ListContentManualText
              :tdhWordsItems="tdhWordsItems"
              scope="description"
            />
            <section>
              <h5 class="c-title--small">使用頻度の高いキーワード</h5>
              <ListContentManualKeyword
                :tdhWordsItems="tdhWordsItems"
                scope="description"
              />
            </section>
          </div>
        </div>
      </section>
    </div>
  </section>
</template>
<script>
import ListContentManualText from '@/components/contentPlans/ListContentManualText';
import ListContentManualKeyword from '@/components/contentPlans/ListContentManualKeyword';
export default {
  props: {
    keyword: {
      type: String,
      required: true
    },
    tdhWordsItems: {
      type: Array,
      required: true
    }
  },
  components: {
    ListContentManualText,
    ListContentManualKeyword
  }
};
</script>
